import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { LOGO_URL, SMOKE_TEXTURE, scrollToSection } from "@/data/catalog";

const LINES = [
  { text: "WE DON'T JUST", red: false },
  { text: "MARKET.", red: false },
  { text: "WE MOBILIZE", red: true },
  { text: "YOUR BRAND.", red: true },
];

const STATS = [
  { value: "700+", label: "services in catalog" },
  { value: "16", label: "networks wired live" },
  { value: "0–6h", label: "typical delivery start" },
  { value: "100%", label: "private. no passwords" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative flex min-h-screen flex-col overflow-hidden bg-[#050505]">
      <div className="absolute inset-0">
        <img src={SMOKE_TEXTURE} alt="" className="h-[120%] w-full object-cover opacity-[0.12] mix-blend-screen" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-transparent to-[#050505]" />
      </div>
      <div className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-[#E11D2E]/[0.07] blur-[140px]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto grid w-full max-w-7xl flex-1 items-center gap-12 px-6 pt-28 pb-16 md:grid-cols-2 lg:px-10"
      >
        <div>
          <h1 className="font-display uppercase leading-[0.98] tracking-tight text-white" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl ${line.red ? "text-[#E11D2E]" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-subcopy"
            className="mt-7 max-w-md text-sm leading-relaxed text-[#8A8A8A] md:text-base"
          >
            Cyber Smoke Media delivers high-impact digital marketing and media promotion across the
            biggest platforms. More visibility. More engagement. More results.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToSection("#services")}
              className="group flex items-center gap-2 bg-[#E11D2E] px-7 py-4 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#F13B47] hover:shadow-[0_0_32px_rgba(225,29,46,0.4)]"
            >
              View services
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToSection("#contact")}
              className="border border-[#333333] px-7 py-4 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-[#E11D2E]"
            >
              Talk to us
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-4 md:mt-0"
        >
          <motion.div style={{ y: imgY }} className="relative flex items-center justify-center">
            <div className="absolute h-40 w-3/4 rounded-full bg-[#E11D2E]/[0.12] blur-[90px]" />
            <img src={LOGO_URL} alt="Cyber Smoke Media" data-testid="hero-artwork" className="relative w-full max-w-lg object-contain" />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          data-testid="hero-stats"
          className="grid grid-cols-2 gap-px border border-[#1F1F1F] bg-[#1F1F1F] md:col-span-2 lg:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#050505] p-5">
              <div className="font-mono text-2xl font-600 text-white">{s.value}</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-[#8A8A8A] transition-colors hover:text-[#E11D2E]"
        aria-label="Scroll down"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.button>
    </section>
  );
}
